<template>
  <div class="preview">
    <BattleplanCard :battleplan="battleplan" :battle-pack="battlePack" />

    <StPanel title="Scoring widgets">
      <template #actions>
        <span class="round-label">Round</span>
        <StSegmentedControl v-model="round" :options="roundOptions" size="sm" />
      </template>
      <ScoringControls
        v-if="battleplan.scoringRules?.length"
        :rules="battleplan.scoringRules"
        :scoring="scoring"
        :round="round"
        :resources="resources"
        @update:scoring="scoring = $event"
        @update:resources="resources = $event"
      />
      <p v-else class="scoring-empty">
        No scoring rules yet — add them on the Rules tab to preview the in-game widgets.
      </p>
    </StPanel>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import type { IBattleplan, BattlePack } from '@scrolldex/shared';
import BattleplanCard from './BattleplanCard.vue';
import ScoringControls from './ScoringControls.vue';
import { StPanel, StSegmentedControl, type SegmentOption } from './ui/index.js';

const props = defineProps<{ battleplan: IBattleplan; battlePack: BattlePack }>();

const round = ref<number>(1);
const roundOptions: SegmentOption[] = Array.from({ length: 5 }, (_, i) => ({
  value: i + 1,
  label: `R${i + 1}`,
}));
const scoring = ref<Record<string, boolean | number>>({});
const resources = ref<Record<string, number>>({});

watch(
  () => props.battleplan,
  () => {
    scoring.value = {};
    resources.value = {};
  },
);
</script>

<style scoped>
.preview {
  display: flex;
  flex-direction: column;
  gap: var(--st-space-4);
}
/* The pane (.st-editor-card__pane--pad, merged onto this root) is a flex item with
   flex:1 + min-height:0, so without this it would shrink its children to fit and
   the StPanel — which clips with overflow:hidden — would collapse to an empty
   sliver. Keep natural heights and let the pane scroll instead. */
.preview > * {
  flex-shrink: 0;
}
.round-label {
  font-size: var(--st-text-xs);
  font-weight: var(--st-weight-semibold);
  text-transform: uppercase;
  letter-spacing: var(--st-tracking-wide);
  color: var(--st-text-subtle);
}
.scoring-empty {
  font-size: var(--st-text-sm);
  color: var(--st-text-muted);
  font-style: italic;
}
</style>
